/**
 * ECS Query system with caching.
 *
 * Queries filter entities by required/excluded/optional component sets.
 * Results are cached by query signature and invalidated when entity archetype changes.
 */

import type { ComponentDef, TagDef } from './Component.js';
import type { World } from './World.js';

// Hoisted sort comparator — _evaluateInner runs per query per frame.
const _ascendingIds = (a: number, b: number): number => a - b;

// ─── Query Entity ───────────────────────────────────────────────────────────────

/**
 * Thin wrapper around an entity id used by query iteration.
 *
 * `id` is mutable so QueryResult can reuse a pool of instances across
 * evaluations instead of allocating a fresh QueryEntity per matched
 * entity per evaluate. Callers that need to retain the entity beyond
 * the iteration scope MUST read `.id` — stashing the QueryEntity itself
 * is unsafe because the next evaluate will reassign it.
 */
export class QueryEntity {
  public id: number;
  private _world: World;

  constructor(id: number, world: World) {
    this.id = id;
    this._world = world;
  }

  get<T>(def: ComponentDef): T {
    return this._world.getComponent(this.id, def) as T;
  }

  tryGet<T>(def: ComponentDef): T | undefined {
    return this._world.tryGetComponent(this.id, def) as T | undefined;
  }

  has(tag: TagDef): boolean {
    return this._world.hasTag(this.id, tag);
  }
}

// ─── Query Result ───────────────────────────────────────────────────────────────

export class QueryResult implements Iterable<QueryEntity> {
  /**
   * Pooled QueryEntity slots. Length equals the number of matches from
   * the last `_evaluate`; capacity (the backing array's length prior to
   * truncation) grows as the high-water mark rises. Unused slots past
   * `_matchCount` stay around as reusable carriers for the next call.
   */
  private _entities: QueryEntity[] = [];
  private _matchCount = 0;
  private _excludeDefs: (ComponentDef | TagDef)[] = [];
  private _optionalDefs: ComponentDef[] = [];
  /** Scratch used to avoid allocating a fresh `number[]` inside _evaluate. */
  private _aliveScratch: number[] = [];
  /** Re-entry guard — fires when `_evaluate` is called while a prior
   *  `_evaluate` call's results (in `_aliveScratch` and `_entities`) are
   *  still being iterated by an outer for-of on the same instance. The
   *  contract is documented but was previously unenforced; nested-iteration
   *  bugs would silently corrupt one of the iterations. */
  private _evaluating = false;

  constructor(
    private _world: World,
    private _requiredDefs: ComponentDef[],
  ) {}

  get length(): number {
    this._evaluate();
    return this._matchCount;
  }

  without(...defs: (ComponentDef | TagDef)[]): QueryResult {
    const clone = new QueryResult(this._world, this._requiredDefs);
    clone._excludeDefs = [...this._excludeDefs, ...defs];
    clone._optionalDefs = [...this._optionalDefs];
    return clone;
  }

  optional(...defs: ComponentDef[]): QueryResult {
    const clone = new QueryResult(this._world, this._requiredDefs);
    clone._excludeDefs = [...this._excludeDefs];
    clone._optionalDefs = [...this._optionalDefs, ...defs];
    return clone;
  }

  [Symbol.iterator](): Iterator<QueryEntity> {
    this._evaluate();
    // We no longer snapshot the array. Re-entering the iterator (nested
    // for-of) would reuse the same pooled QueryEntity slots — callers
    // must not nest iterations on the same QueryResult. This is the
    // documented contract and matches how every call site uses it.
    const entities = this._entities;
    const end = this._matchCount;
    let index = 0;
    return {
      next(): IteratorResult<QueryEntity> {
        if (index < end) {
          return { value: entities[index++], done: false };
        }
        return { value: undefined as any, done: true };
      },
    };
  }

  private _evaluate(): void {
    if (this._evaluating) {
      // Nested re-entry on the same QueryResult instance would clobber
      // `_aliveScratch` (refilled in place by `getAliveEntities`) and
      // corrupt the outer iteration. Warn loudly — callers must construct
      // a separate QueryResult or clone() instead of nesting.
      console.warn('[QueryResult] Nested _evaluate detected on the same instance. Allocate a separate QueryResult or use without/optional() to clone before nesting.');
      return;
    }
    this._evaluating = true;
    try {
      this._evaluateInner();
    } finally {
      this._evaluating = false;
    }
  }

  private _evaluateInner(): void {
    // Candidate narrowing: iterate only the SMALLEST required component/tag
    // membership index instead of every alive entity — a query matching 12
    // entities in a 10k-entity world now touches ~12 candidates. Sorted
    // ascending for a deterministic result order. Falls back to the full
    // alive scan only for queries with no required defs.
    let smallest: ReadonlySet<number> | null = null;
    for (const def of this._requiredDefs) {
      const index = def.isTag
        ? this._world.getTagIndex(def as unknown as TagDef)
        : this._world.getComponentIndex(def as ComponentDef);
      if (!index || index.size === 0) {
        this._matchCount = 0; // a required def nobody holds — no matches
        return;
      }
      if (!smallest || index.size < smallest.size) smallest = index;
    }
    let entityIds: number[];
    if (smallest) {
      entityIds = this._aliveScratch;
      entityIds.length = 0;
      let sorted = true;
      let prev = -1;
      for (const eid of smallest) {
        entityIds.push(eid);
        if (eid < prev) sorted = false;
        prev = eid;
      }
      // Index Sets fill in id order almost always (ids are handed out
      // sequentially), so the comparator sort is usually a no-op that still
      // costs O(n) callback invocations per query per frame — skip it when
      // the O(n) scan above already proved the order ascending.
      if (!sorted) entityIds.sort(_ascendingIds);
    } else {
      // Scratch-array variant — no fresh number[] per call.
      entityIds = this._world.getAliveEntities(this._aliveScratch);
    }

    let matchIdx = 0;
    const required = this._requiredDefs;
    const excluded = this._excludeDefs;
    for (let i = 0; i < entityIds.length; i++) {
      const eid = entityIds[i];
      // Alive filter (index sets keep destroy-window entities until the
      // destroy flush) + required + excluded checks in ONE World call that
      // resolves the entity's component/tag maps once, instead of
      // isAlive/hasComponent/hasTag each re-hitting the maps per def.
      if (!this._world._matchesArchetype(eid, required, excluded)) continue;

      // Reuse an existing pooled slot when available; allocate only when
      // the high-water mark grows. Mutating `id` instead of allocating
      // saves one QueryEntity per match per evaluate (significant when
      // queries match hundreds of entities at 60+ Hz).
      const existing = this._entities[matchIdx];
      if (existing) {
        existing.id = eid;
      } else {
        this._entities[matchIdx] = new QueryEntity(eid, this._world);
      }
      matchIdx++;
    }

    this._matchCount = matchIdx;
  }
}
